import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { welcomeOverlayPref } from "../../utils/prefCookie";
import { Button } from "../misc/Button";
import Checkbox from "../misc/Checkbox";
import { Share2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface WelcomeListingProps {
    welcome: boolean;
    type?:
        | "firstListing"
        | "listingCampaign"
        | "promotionCampaign"
        | "listingPromotionCampaign"
        | null;
    onClose?: () => void;
}

const WelcomeListing: React.FC<WelcomeListingProps> = ({
    welcome,
    type,
    onClose,
}) => {
    const navigate = useNavigate();
    // Always starts unchecked: HomeScreen does not mount this modal once the
    // opt-out cookie is set, so there is no saved-checked state to restore.
    const [hideWelcome, setHideWelcome] = React.useState(false);

    // Decide text & action based on type
    let headline = "Welcome to Marabot!";
    let message = "";
    let buttonText = "";
    let actionPath = "";
    let buttonTextTwo = "";
    let actionPathTwo = "";

    switch (type) {
        case "listingCampaign":
            message =
                "You’ve created a listing — now create a campaign to schedule your posts.";
            buttonText = "Create Listing Campaign";
            actionPath = "/listings";
            break;
        case "promotionCampaign":
            message =
                "You have created a promotion — create a promotional campaign to schedule posts.";
            buttonText = "Create Promotion Campaign";
            actionPath = "/promotions";
            break;
        case "listingPromotionCampaign":
            message =
                "You have created listing(s) and a promotion — create campaigns to schedule posts.";
            buttonText = "Create Listing Campaign";
            actionPath = "/listings";
            buttonTextTwo = "Create Promotion Campaign";
            actionPathTwo = "/promotions";
            break;
        case "firstListing":
        default:
            message =
                "We’re glad you’re here — create your first listing to get started!";
            buttonText = "Create Your First Listing";
            actionPath = "/create-listing";
            break;
    }

    const handlePrimary = () => {
        if (type === "firstListing" && welcome) {
            sessionStorage.removeItem("welcome");
        }
        navigate(actionPath);
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-40"
            >
                <motion.div
                    initial={{ scale: 0.9, y: 50 }}
                    animate={{ scale: 1, y: 0 }}
                    exit={{ scale: 0.9, y: 50 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    className="bg-white rounded-2xl p-8 max-w-md w-full relative shadow-2xl"
                >
                    {/* X to close */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    >
                        <X size={24} />
                    </button>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                    >
                        <h2 className="text-3xl font-bold mb-4 text-center text-primary">
                            {headline}
                        </h2>
                    </motion.div>

                    <motion.p
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-center mb-8 text-gray-600"
                    >
                        {message}
                    </motion.p>

                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-4"
                    >
                        <Button onClick={handlePrimary} className="w-full">
                            {buttonText}
                        </Button>

                        {buttonTextTwo.length > 0 &&
                            actionPathTwo.length > 0 && (
                                <Button
                                    variant="secondary"
                                    onClick={() => navigate(actionPathTwo)}
                                    className="w-full"
                                >
                                    {buttonTextTwo}
                                </Button>
                            )}

                        {type === "firstListing" && (
                            <Button
                                variant="secondary"
                                onClick={() =>
                                    navigate("/help", {
                                        state: { tab: "tutorials" },
                                    })
                                }
                                className="w-full"
                            >
                                <Share2 className="mr-2 h-5 w-5" />
                                See How It Works
                            </Button>
                        )}

                        <div className="flex items-center justify-center gap-2 pt-2">
                            <Checkbox
                                variant="radix"
                                id="hide-welcome-modal"
                                checked={hideWelcome}
                                onChange={(checked) => {
                                    setHideWelcome(checked);
                                    welcomeOverlayPref.setHidden(checked);
                                }}
                            />
                            <label
                                htmlFor="hide-welcome-modal"
                                className="text-sm text-gray-600 cursor-pointer"
                            >
                                Don’t show this again
                            </label>
                        </div>
                    </motion.div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default WelcomeListing;
